import React, {useEffect, useState} from 'react';
import {getHelmetForMetaTags} from '../../utils/epicGamesUtils';
import {useLocation, useNavigate} from 'react-router-dom';
import {withLocalization} from '@epic-core/localization';
import {PageData, useCmsData, cmsApi, AppSharedKeys} from 'epic-ue-shared';
import {generateRoutePath} from 'epic-ue-locale-mode';
import LoadIndicator from '../../components/common/loading/LoadIndicator';
import {UeThemeProvider, ueDarkTheme, useBreakpointDown, useBreakpointOnly} from 'epic-ue-theme';
import {GridContainer, ImageHero, ImageHeroProps, Typography, VerticalSpacer} from 'epic-ue-ui';
import {Pagination} from 'epic-ue-feed';
import Grid from '@material-ui/core/Grid';
import {FilteredResults, Styled} from './JobsView.styles';
import {useJobResults} from './jobs.hooks';
import {JobList} from './JobsView.list';
import {MobileFilters} from './mobile/MobileFilters';
import {JobFilters, useFilterFns} from './jobs.callbacks';
import {CurrentFilterTags} from './filters/CurrentFilterTags';
import {JobsViewFilters} from './filters/JobsViewFilters';
import {
    getResultsOffset,
    generateFiltersQueryParams,
    getInitialFiltersFromQuery,
    getUrlQueryParams
} from './jobs.utils';
import {jobFetcher, JobsRes} from './jobs.fetch';
import root from 'window-or-global';
import {isError} from '@epic-mw/error-types';
import Error404 from '../error/Error404';

const cmsUrlPattern = '/careers/jobs';

export interface JobsColumnProps {
    position?: string;
    company?: string;
    department?: string;
    location?: string;
}

export interface JobsPageData extends PageData {
    header?: ImageHeroProps;
    jobPageSize?: number;
    positionsLabel?: string;
    noResults?: string;
    searchLabel?: string;
    filterLabel?: string;
    mobileSearchLabel?: string;
    spotlightHoverLabel?: string;
    applyButtonlabel?: string;
    productLabel?: string;
    typeLabel?: string;
    remoteLabel?: string;
    maintenanceMsg?: string;
    detailImageMapping?: {
        corporate?: string;
        engine?: string;
        houseparty?: string;
        metahuman?: string;
        default?: string;
    };
    companyDetailImageMapping?: {
        threeLateral?: string;
        psyonix?: string;
    };
    columns?: JobsColumnProps;
    hideRemoteFilter?: boolean;
}

const JobsView = ({getMessage}: {getMessage: (key: string) => string}): JSX.Element => {
    const location = useLocation();
    const {cmsLoading, cmsLoadedEmpty, pageData} = useCmsData<JobsPageData>(cmsUrlPattern);
    const {
        jobPageSize = 50,
        positionsLabel,
        noResults,
        filterLabel,
        mobileSearchLabel,
        header,
        spotlightHoverLabel,
        productLabel,
        typeLabel,
        remoteLabel,
        columns,
        maintenanceMsg,
        searchLabel,
        hideRemoteFilter
    } = pageData;

    const currentQuery = getUrlQueryParams();
    const queryPage = currentQuery.page;
    const queryKeyword = currentQuery.keyword;

    const [jobResultsPage, setJobResultsPage] = useState(+(queryPage || 1));
    const [search, setSearch] = useState(`${queryKeyword || ''}`);
    const [openedFilterBarMobile, setOpenedFilterBarMobile] = useState(false);
    const navigate = useNavigate();
    const isSm = useBreakpointDown('sm');
    const isMdDown = useBreakpointDown('md');
    const isMdOnly = useBreakpointOnly('md');

    const {jobs = {}, isLoading} = useJobResults(
        {
            limit: jobPageSize,
            skip: getResultsOffset(jobResultsPage, jobPageSize),
            page: jobResultsPage,
            ...currentQuery
        },
        hideRemoteFilter
    );
    const {hits, counts, total} = jobs;

    const initFilters = getInitialFiltersFromQuery(hideRemoteFilter);
    const [jobsFilters, setJobsFilters] = useState<JobFilters>(initFilters);
    const {toggleCheckboxFilter, setLocationFilter, removeFilter} = useFilterFns(
        jobsFilters,
        setJobsFilters,
        jobResultsPage,
        setJobResultsPage
    );

    const filtersAreSame = JSON.stringify(initFilters) === JSON.stringify(jobsFilters);
    const queryParams = generateFiltersQueryParams(
        jobsFilters,
        search,
        jobResultsPage,
        hideRemoteFilter
    );

    //when filters, search, or page state changes update the URL
    useEffect(() => {
        if (filtersAreSame && !search && !queryKeyword && jobResultsPage === 1) {
            //when the only queryParam is page we can skip the navigate
        } else {
            navigate(generateRoutePath(`careers/jobs?${queryParams}`));
        }
    }, [navigate, search, filtersAreSame, queryParams, queryKeyword, jobResultsPage]);

    //Listen for page change in URI to laod new results
    useEffect(() => {
        getUrlQueryParams().page && setJobResultsPage(Number(getUrlQueryParams().page));
    }, [location]);

    //Prevent background body scroll, when filters overlay is opened on Mobile
    useEffect(() => {
        isSm && openedFilterBarMobile
            ? (root.document.body.style.overflow = 'hidden')
            : (root.document.body.style.overflow = 'initial');
    }, [isSm, openedFilterBarMobile]);

    if (cmsLoading) {
        return (
            <div className="jobs">
                {getHelmetForMetaTags(pageData)}
                <LoadIndicator />
            </div>
        );
    }

    if (cmsLoadedEmpty) {
        return <Error404 />;
    }

    return (
        <UeThemeProvider theme={ueDarkTheme}>
            <Styled>
                {getHelmetForMetaTags(pageData)}
                <ImageHero {...header} />

                <VerticalSpacer variant="layout" />

                <GridContainer>
                    <Grid container direction="row" justifyContent="center" spacing={3}>
                        <Grid item xs={12} md={12} style={!isSm ? {paddingRight: 0} : {}}>
                            {isSm && !maintenanceMsg && (
                                <MobileFilters
                                    setOpenedFilterBarMobile={setOpenedFilterBarMobile}
                                    search={search}
                                    setSearch={setSearch}
                                    setJobResultsPage={setJobResultsPage}
                                    filterLabel={filterLabel}
                                    mobileSearchLabel={mobileSearchLabel}
                                />
                            )}
                            {isMdOnly && <VerticalSpacer variant="layout" />}

                            <JobsViewFilters
                                hidden={!!maintenanceMsg}
                                counts={counts}
                                jobsFilters={jobsFilters}
                                search={search}
                                setSearch={setSearch}
                                setJobResultsPage={setJobResultsPage}
                                toggleCheckboxFilter={toggleCheckboxFilter}
                                setLocationFilter={setLocationFilter}
                                openedFilterBarMobile={openedFilterBarMobile}
                                setOpenedFilterBarMobile={setOpenedFilterBarMobile}
                                filterLabel={filterLabel}
                                productLabel={productLabel}
                                typeLabel={typeLabel}
                                remoteLabel={remoteLabel}
                                columnLabels={columns}
                                searchLabel={searchLabel}
                                hideRemoteFilter={hideRemoteFilter}
                            />

                            <VerticalSpacer variant="eyebrow" />
                        </Grid>
                    </Grid>

                    {!maintenanceMsg &&
                        (isSm ? (
                            <>
                                {total && total > 0 ? (
                                    <Typography variant="p" className="job-results-count">
                                        {total &&
                                            `${total} ${
                                                positionsLabel || getMessage('epic.jobs.positions')
                                            }`}
                                    </Typography>
                                ) : (
                                    ''
                                )}
                                <VerticalSpacer variant="eyebrow" />
                                <CurrentFilterTags
                                    jobsFilters={jobsFilters}
                                    removeFilter={removeFilter}
                                />
                                <VerticalSpacer variant="heading" />
                            </>
                        ) : (
                            <>
                                <Grid container direction="row" justifyContent="center" spacing={3}>
                                    <Grid item md={10}>
                                        <CurrentFilterTags
                                            jobsFilters={jobsFilters}
                                            removeFilter={removeFilter}
                                        />
                                    </Grid>
                                    <Grid item md={2} style={{paddingRight: 0}}>
                                        {total && total > 0 ? (
                                            <Typography variant="p" className="job-results-count">
                                                {total &&
                                                    `${total} ${
                                                        positionsLabel ||
                                                        getMessage('epic.jobs.positions')
                                                    }`}
                                            </Typography>
                                        ) : (
                                            ''
                                        )}
                                    </Grid>
                                </Grid>
                                <VerticalSpacer variant="heading" />
                            </>
                        ))}

                    <FilteredResults>
                        {!isMdDown && hits && hits.length > 0 ? (
                            <Grid
                                className="filtered-result-heading filtered-result-row"
                                container
                                direction="row"
                                justifyContent="center"
                                spacing={3}>
                                <Grid item lg={4}>
                                    <Grid
                                        container
                                        direction="row"
                                        justifyContent="center"
                                        spacing={3}>
                                        <Grid item xs={1}></Grid>
                                        <Grid item xs={11}>
                                            <Typography variant="p">
                                                {columns?.position ||
                                                    getMessage('epic.jobs.column.position')}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item lg={2} style={{paddingBottom: 0}}>
                                    <Typography variant="p">
                                        {columns?.company || getMessage('epic.jobs.column.company')}
                                    </Typography>
                                </Grid>
                                <Grid item lg={3} style={{paddingBottom: 0}}>
                                    <Typography variant="p">
                                        {columns?.department ||
                                            getMessage('epic.jobs.column.department')}
                                    </Typography>
                                </Grid>
                                <Grid item lg={3} style={{paddingBottom: 0}}>
                                    <Typography variant="p">
                                        {columns?.location ||
                                            getMessage('epic.jobs.column.location')}
                                    </Typography>
                                </Grid>
                            </Grid>
                        ) : (
                            ''
                        )}

                        <JobList
                            maintenanceMsg={maintenanceMsg}
                            isLoading={isLoading}
                            jobs={hits}
                            countryFilter={jobsFilters.country}
                            noResults={noResults}
                            spotlightHoverLabel={spotlightHoverLabel}
                        />

                        <Pagination
                            paging={{
                                page: 1,
                                pageSize: jobPageSize,
                                start: 0,
                                end: 12,
                                length: total
                            }}
                        />
                    </FilteredResults>
                </GridContainer>
                <VerticalSpacer variant="section" />
            </Styled>
        </UeThemeProvider>
    );
};

export const getJobsViewInitialSharedValues = async (): Promise<any> => {
    const pageData: JobsPageData = await cmsApi.getPageData(cmsUrlPattern);

    const responses: {key: any; value: any}[] = [
        {
            key: AppSharedKeys.CmsStore,
            value: {
                [cmsUrlPattern]: pageData
            }
        }
    ];

    const currentQuery = getUrlQueryParams();
    const queryPage = currentQuery.page;
    const jobResultsPage = +(queryPage || 1);
    const jobPageSize = pageData?.jobPageSize || 50;
    const serverQuery = {
        limit: jobPageSize,
        skip: getResultsOffset(jobResultsPage, jobPageSize),
        page: jobResultsPage,
        ...currentQuery
    };
    const stringified = JSON.stringify(serverQuery);
    let jobData: JobsRes | undefined;

    try {
        jobData = await jobFetcher.getJobs(stringified);
    } catch (e) {
        console.error('Failed to SSR job data', isError(e) && e.message);
    }

    responses.push({
        key: AppSharedKeys.JobsStore,
        value: {
            jobData,
            serverQuery,
            job: null
        }
    });

    return responses;
};

export default withLocalization(JobsView);
